import { TFunction } from 'i18next';
import _get from 'lodash/get';
import { FormTemplate } from 'graphql-common';
import TextCell from '@lib/components/Table/commonComponents/TextCell/TextCell';
import ChipCell from '@lib/components/Table/commonComponents/ChipCell/ChipCell';
import TextLinkCell from '@lib/components/Table/commonComponents/TextLinkCell/TextLinkCell';
import { CellProps, Column } from '@lib/components/Table/types';
import { colorToChipType } from '@lib/components/Chip/utils/colorEnum';
import FormTemplateFieldNames from '@lib/enums/fieldNames/formTemplateFieldNames';
import { getFormattedDate } from '@lib/utils/dateTimeHelpers';
import { APP_URLS } from 'constants/urls';

type ColumnsArgs = {
  t: TFunction<'translation', undefined>;
};

export default function getFormTemplatesColumns({
  t,
}: ColumnsArgs): Column<FormTemplate>[] {
  return [
    {
      id: 'name',
      Header: t('name'),
      accessor: FormTemplateFieldNames.Name,
      Cell: (props: CellProps<FormTemplate>) => {
        const formTemplateId = props?.item.id;
        const formTemplateVersionId = props?.item.currentVersion?.id || '';
        return (
          <TextLinkCell
            {...props}
            to={APP_URLS.dashboard.formTemplates.viewVersion.getDynamicPath({
              pathParts: {
                id: formTemplateId,
                versionId: formTemplateVersionId,
              },
            })}
          />
        );
      },
      sortable: true,
      maxWidth: 350,
    },
    {
      id: 'categoryName',
      Header: t('form-category'),
      Cell: (props: CellProps<FormTemplate>) => {
        const label = _get(
          props,
          `item.${FormTemplateFieldNames.Category}.name`,
        );
        const type = colorToChipType(
          _get(props, `item.${FormTemplateFieldNames.Category}.color`),
        );
        if (!label || !type) return '-';
        return <ChipCell label={label} type={type} leftIcon="circle" />;
      },
      maxWidth: 250,
      minWidth: 130,
      width: 200,
      sortable: true,
    },
    {
      id: 'versionsCount',
      Header: t('versions'),
      accessor: 'versionsCount',
      Cell: (props: CellProps<FormTemplate>) => <TextCell {...props} />,
      maxWidth: 170,
      minWidth: 100,
      width: 130,
      sortable: true,
    },
    {
      id: 'visibleLinkedTasksCount',
      Header: t('linked-tasks'),
      accessor: 'visibleLinkedTasksCount',
      Cell: (props: CellProps<FormTemplate>) => <TextCell {...props} />,
      headerTooltipInfo: {
        tooltipBody: t('linked-tasks-tooltip'),
        icon: 'info',
        symbolsOutlined: true,
      },
      maxWidth: 170,
      minWidth: 100,
      width: 130,
      sortable: true,
    },
    {
      id: 'createdAt',
      Header: t('date-created'),
      accessorFn: ({ item }: CellProps<FormTemplate>) =>
        getFormattedDate(item.createdAt),
      Cell: (props: CellProps<FormTemplate>) => <TextCell {...props} />,
      maxWidth: 170,
      minWidth: 130,
      width: 170,
      sortable: true,
    },
  ];
}
